import { useEffect, useState } from 'react';
import { Building2, Plus } from 'lucide-react';
import { Button, Modal } from '../../ui';
import type { CreateClientInput } from '../../gql/types';
import { TextField } from '../shared/FormField';
import { InlineBanner } from '../shared/InlineBanner';

// Create form for a client/tenant (US-8.1.1, W1). Name is required; dataResidency is optional
// free text (e.g. "au"). Metadata is intentionally omitted for now (advanced field). Save is
// blocked until a non-empty name is present; the server error is surfaced inline in the modal
// so failures are never silently swallowed.

export interface ClientFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  loading?: boolean | undefined;
  /** Server-side error from the create mutation, surfaced inline. */
  errorMessage?: string | undefined;
  onSubmit: (input: CreateClientInput) => void;
}

export function ClientFormModal({
  isOpen,
  onClose,
  loading = false,
  errorMessage,
  onSubmit,
}: ClientFormModalProps) {
  const [name, setName] = useState('');
  const [dataResidency, setDataResidency] = useState('');
  // Show the required-name error only after a submit attempt, not on first render.
  const [attempted, setAttempted] = useState(false);

  // (Re)seed the form whenever the modal opens so a reopened modal starts clean.
  useEffect(() => {
    if (!isOpen) return;
    setName('');
    setDataResidency('');
    setAttempted(false);
  }, [isOpen]);

  const trimmedName = name.trim();
  const valid = trimmedName.length > 0;

  const handleSubmit = () => {
    setAttempted(true);
    if (!valid) return;
    const trimmedResidency = dataResidency.trim();
    const input: CreateClientInput = {
      name: trimmedName,
      ...(trimmedResidency === '' ? {} : { dataResidency: trimmedResidency }),
    };
    onSubmit(input);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="New client"
      subtitle="Onboard a tenant. Applications and API keys are added from the client's detail view."
      icon={<Building2 className="h-7 w-7" aria-hidden="true" />}
      footer={
        <>
          <Button variant="secondary" fullWidth onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            fullWidth
            loading={loading}
            disabled={!valid}
            onClick={handleSubmit}
            leftIcon={<Plus className="h-4 w-4" aria-hidden="true" />}
          >
            Create client
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {errorMessage && (
          <InlineBanner tone="error" testId="client-form-error">
            Couldn’t create the client: {errorMessage}
          </InlineBanner>
        )}

        <TextField
          label="Client name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Acme Corp"
          error={attempted && !valid ? 'Enter a name for the client.' : undefined}
        />

        <TextField
          label="Data residency"
          value={dataResidency}
          onChange={(e) => setDataResidency(e.target.value)}
          placeholder="Optional — e.g. au"
          hint="Region code where this tenant's data should reside. Leave blank if unconstrained."
        />
      </div>
    </Modal>
  );
}
